import { HomeReview, ReviewContactDetails } from '../types';

export type ReviewMetadataServiceDependencies = {
  database?: {
    updateReview?: (
      reviewId: number,
      updates: Record<string, unknown>
    ) => Promise<unknown>;
  } | null;
  onlineApi: {
    updateReviewNotes(reviewId: number, notes: string): Promise<HomeReview>;
    updateReviewOnDemandProjects(
      reviewId: number,
      onDemandProjects: string
    ): Promise<HomeReview>;
    updateReviewDetails(
      reviewId: number,
      details: ReviewContactDetails
    ): Promise<HomeReview>;
  };
  offlineReviewService?: {
    updateOfflineReview?(
      reviewId: number,
      updates: Partial<HomeReview>
    ): Promise<HomeReview>;
  } | null;
  useOfflineWorkflow?: () => boolean;
};

export class ReviewMetadataService {
  constructor(private readonly deps: ReviewMetadataServiceDependencies) {}

  async updateReviewNotes(reviewId: number, notes: string): Promise<HomeReview> {
    if (this.deps.useOfflineWorkflow?.()) {
      return this.updateOfflineReview(reviewId, { notes }, 'Offline review service not available');
    }

    const updatedReview = await this.deps.onlineApi.updateReviewNotes(reviewId, notes);

    await this.cacheReviewMetadata(
      reviewId,
      {
        notes: updatedReview.notes ?? notes,
        syncStatus: 'synced',
        syncError: null,
      },
      '[ReviewService] Failed to cache updated review notes offline:'
    );

    return updatedReview;
  }

  async updateReviewOnDemandProjects(
    reviewId: number,
    onDemandProjects: string
  ): Promise<HomeReview> {
    if (this.deps.useOfflineWorkflow?.()) {
      return this.updateOfflineReview(
        reviewId,
        { onDemandProjects },
        'Offline review service not available'
      );
    }

    const updatedReview = await this.deps.onlineApi.updateReviewOnDemandProjects(
      reviewId,
      onDemandProjects
    );

    await this.cacheReviewMetadata(
      reviewId,
      {
        onDemandProjects: updatedReview.onDemandProjects ?? onDemandProjects,
        syncStatus: 'synced',
        syncError: null,
      },
      '[ReviewService] Failed to cache updated On Demand Projects offline:'
    );

    return updatedReview;
  }

  async updateReviewDetails(
    reviewId: number,
    details: ReviewContactDetails,
    options: { forceOnline?: boolean } = {}
  ): Promise<HomeReview> {
    if (this.deps.useOfflineWorkflow?.() && !options.forceOnline) {
      return this.updateOfflineReview(
        reviewId,
        details,
        'Offline review service not available'
      );
    }

    const updatedReview = await this.deps.onlineApi.updateReviewDetails(reviewId, details);
    await this.cacheReviewMetadata(
      reviewId,
      {
        memberName: updatedReview.memberName ?? details.memberName,
        memberEmail: updatedReview.memberEmail ?? details.memberEmail ?? null,
        homeManagerName: updatedReview.homeManagerName ?? details.homeManagerName ?? null,
        homeManagerEmail: updatedReview.homeManagerEmail ?? details.homeManagerEmail ?? null,
        syncStatus: 'synced',
        syncError: null,
      },
      '[ReviewService] Failed to cache updated review details offline:'
    );

    return updatedReview;
  }

  private async cacheReviewMetadata(
    reviewId: number,
    updates: Record<string, unknown>,
    warningMessage: string
  ): Promise<void> {
    if (!this.deps.database?.updateReview) {
      return;
    }

    try {
      await this.deps.database.updateReview(reviewId, updates);
    } catch (cacheError) {
      console.warn(warningMessage, cacheError);
    }
  }

  private async updateOfflineReview(
    reviewId: number,
    updates: Partial<HomeReview>,
    unavailableMessage: string
  ): Promise<HomeReview> {
    if (!this.deps.offlineReviewService?.updateOfflineReview) {
      throw new Error(unavailableMessage);
    }

    return this.deps.offlineReviewService.updateOfflineReview(reviewId, updates);
  }
}
